// LAP-550 V1.2 — Tableau d'actifs commun pour les pages détail (DataGrid stylé)
// autoHeight pour s'adapter au nombre de lignes (évite le grand vide quand peu de lignes)
//
// LAP-551 — Recherche, tri et filtres.
// Deux régimes, selon la taille du marché :
//   • `paginationMode='client'` : toutes les lignes sont chargées, le DataGrid
//     trie/filtre/recherche lui-même. La recherche alimente `quickFilterValues`
//     du modèle de filtre, ce qui garde le compteur du pied de page juste.
//   • `paginationMode='server'` : seule une page est chargée. Trier ou filtrer
//     côté client ne porterait que sur les 25 lignes visibles — c'est le bug
//     historique. On bascule donc `sortingMode`/`filterMode` sur 'server' et on
//     remonte recherche, tri et filtres au parent, qui les envoie à l'API.
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
} from 'Components/LifeDataGrid'
import EmptyState from './EmptyState'
import { sharedDataGridSx, searchFieldSx } from './theme'

// Défini au niveau module (et non dans le rendu) : un composant recréé à chaque
// rendu serait démonté/remonté par le DataGrid, et le champ de recherche
// perdrait le focus à chaque frappe.
function AssetTableToolbar({ search, onSearchChange, placeholder, showFilters, extra }) {
  const { t } = useTranslation('layout')
  return (
    <GridToolbarContainer>
      <TextField
        size="small"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={placeholder}
        sx={searchFieldSx}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: search ? (
            <InputAdornment position="end">
              <Tooltip title={t('investments.detail.searchClear', 'Effacer')}>
                <IconButton size="small" onClick={() => onSearchChange('')}>
                  <CloseIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.6)' }} />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ) : null,
        }}
      />
      <GridToolbarColumnsButton />
      {showFilters && <GridToolbarFilterButton />}
      <GridToolbarDensitySelector />
      {extra ? <Box sx={{ ml: 'auto' }}>{extra}</Box> : null}
    </GridToolbarContainer>
  )
}

export default function AssetTable({
  rows = [],
  columns = [],
  loading = false,
  pageSize = 25,
  minHeight = 220,
  emptyLabel = 'Aucune donnée',
  getRowId,
  onRowClick,
  // LAP — sélection multiple optionnelle (cases à cocher) pour les actions
  // groupées des pages détail (ajout au portefeuille, favoris…).
  checkboxSelection = false,
  selectionModel,
  onSelectionModelChange,
  // LAP — pagination serveur optionnelle (marchés volumineux). Par défaut
  // 'client' : comportement historique inchangé pour les petits marchés.
  paginationMode = 'client',
  rowCount,
  page,
  onPageChange,
  onPageSizeChange,
  // LAP-551 — barre d'outils : recherche + colonnes + filtres.
  searchable = false,
  search = '',
  onSearchChange,
  searchPlaceholder = 'Rechercher…',
  // Tri / filtres contrôlés (obligatoires en mode serveur)
  sortModel,
  onSortModelChange,
  filterModel,
  onFilterModelChange,
  toolbarExtra,
}) {
  // Quand vide on garde un minHeight pour afficher l'empty state correctement
  const isEmpty = !loading && rows.length === 0
  const isServer = paginationMode === 'server'

  // Mode client : le DataGrid filtre lui-même. On pilote le modèle de filtre
  // pour pouvoir y injecter la recherche rapide sans écraser les filtres de
  // colonne posés par l'utilisateur.
  const [clientFilter, setClientFilter] = useState({ items: [] })
  const quickFilterValues = useMemo(
    () => String(search || '').trim().split(/\s+/).filter(Boolean),
    [search],
  )
  // Mémoïsé : un nouvel objet à chaque rendu relancerait le filtrage en boucle.
  const effectiveFilterModel = useMemo(
    () => (isServer ? filterModel : { ...clientFilter, quickFilterValues }),
    [isServer, filterModel, clientFilter, quickFilterValues],
  )
  const handleFilterModelChange = (model) => {
    if (isServer) {
      if (onFilterModelChange) onFilterModelChange(model)
    } else {
      setClientFilter({ items: model.items, linkOperator: model.linkOperator })
    }
  }

  const serverProps = isServer
    ? {
        paginationMode: 'server',
        rowCount: rowCount ?? 0,
        page,
        onPageChange,
        sortingMode: 'server',
        filterMode: 'server',
      }
    : {}

  const components = useMemo(
    () => ({
      NoRowsOverlay: () => <EmptyState label={emptyLabel} />,
      NoResultsOverlay: () => <EmptyState label={emptyLabel} />,
      ...(searchable ? { Toolbar: AssetTableToolbar } : {}),
    }),
    [emptyLabel, searchable],
  )

  return (
    <Box sx={{ width: '100%', ...(isEmpty ? { height: minHeight } : {}) }}>
      <DataGrid
        autoHeight={!isEmpty}
        sx={{
          ...sharedDataGridSx,
          '.MuiDataGrid-virtualScroller': { minHeight: isEmpty ? minHeight - 56 : undefined },
        }}
        rows={rows}
        columns={columns}
        loading={loading}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
        rowsPerPageOptions={[10, 25, 50, 100]}
        density="standard"
        getRowId={getRowId}
        onRowClick={onRowClick}
        checkboxSelection={checkboxSelection}
        selectionModel={selectionModel}
        onSelectionModelChange={onSelectionModelChange}
        disableSelectionOnClick
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        filterModel={effectiveFilterModel}
        onFilterModelChange={handleFilterModelChange}
        {...serverProps}
        components={components}
        componentsProps={{
          toolbar: {
            search,
            onSearchChange: onSearchChange || (() => {}),
            placeholder: searchPlaceholder,
            showFilters: true,
            extra: toolbarExtra,
          },
        }}
      />
    </Box>
  )
}
